// Catálogo de Modo Diseñar v2: tableros completos, materiales por parte y
// bloques nuevos. El juego (Unity) lee el diseño del tablero de
// localStorage["xima_board_design_v2"] con el formato de serializeBoardDesign.

export const BOARD_DESIGN_STORAGE_KEY = "xima_board_design_v2";
export const MY_BOARDS_STORAGE_KEY = "xima_board_my_presets_v1";
const ASSET_VERSION = "20261014-design-v2";

const publicPath = (relativePath) => {
  const prefix = String(process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  return `${prefix}/${String(relativePath || "").replace(/^\/+/, "")}?v=${ASSET_VERSION}`;
};
export const studioAsset = (relativePath) => publicPath(`design-studio/${relativePath}`);

// ---------------------------------------------------------------------------
// Materiales de las casillas y paredes
// ---------------------------------------------------------------------------

const textureMaterial = (key, label, type, extra = {}) => ({
  id: `tex:${key}`,
  key,
  label,
  type,
  thumb: studioAsset(`materials/${key}.jpg`),
  tile: studioAsset(`tiles/${key}.jpg`),
  wall: studioAsset(`walls/${key}.jpg`),
  ...extra,
});

export const TEXTURE_MATERIALS = [
  textureMaterial("marmol-blanco", "Mármol blanco", "piedra", { finish: "polished" }),
  textureMaterial("marmol-esmeralda", "Mármol esmeralda", "piedra", { finish: "polished" }),
  textureMaterial("marmol-verde-oscuro", "Verde oscuro", "piedra", { finish: "polished" }),
  textureMaterial("marmol-negro", "Mármol negro", "piedra", { finish: "polished" }),
  textureMaterial("azul-noche", "Azul noche", "piedra", { finish: "polished" }),
  textureMaterial("arce", "Arce", "madera", { finish: "varnish" }),
  textureMaterial("nogal", "Nogal", "madera", { finish: "varnish" }),
  textureMaterial("nogal-oscuro", "Nogal oscuro", "madera", { finish: "varnish" }),
  textureMaterial("cesped", "Césped", "naturaleza", { finish: "matte", normal: studioAsset("tiles/cesped-normal.jpg"), wallNormal: studioAsset("walls/cesped-normal.jpg") }),
  textureMaterial("musgo", "Musgo", "naturaleza", { finish: "matte", normal: studioAsset("tiles/musgo-normal.jpg") }),
  textureMaterial("agua", "Agua", "naturaleza", { finish: "mirror", normal: studioAsset("tiles/agua-normal.jpg"), wallNormal: studioAsset("walls/agua-normal.jpg") }),
  textureMaterial("oceano", "Océano", "naturaleza", { finish: "mirror", normal: studioAsset("tiles/oceano-normal.jpg") }),
  textureMaterial("lava", "Lava", "naturaleza", { finish: "lava" }),
  textureMaterial("galaxia", "Galaxia", "naturaleza", { finish: "gloss" }),
];

export const COLOR_MATERIALS = [
  { hex: "#f4f4f5", label: "Blanco" },
  { hex: "#1f2937", label: "Negro" },
  { hex: "#1d4ed8", label: "Azul" },
  { hex: "#e11d48", label: "Rojo" },
  { hex: "#16a34a", label: "Verde" },
  { hex: "#eab308", label: "Amarillo" },
  { hex: "#7c3aed", label: "Morado" },
  { hex: "#f97316", label: "Naranja" },
  { hex: "#0d9488", label: "Turquesa" },
  { hex: "#9ca3af", label: "Gris" },
].map((entry) => ({
  ...entry,
  id: `col:${entry.hex.replace("#", "")}`,
  type: "colores",
}));

// Molduras (parte 5). El color de las letras va con la moldura.
export const TRIM_OPTIONS = [
  { id: "orig", label: "Cristal clásico", swatch: "linear-gradient(135deg,#cfefff,#5fb6dd 60%,#2a6f95)" },
  { id: "gold", label: "Oro pulido", swatch: "linear-gradient(135deg,#fff3c4,#d4af37 55%,#6b4a10)", hex: "#D4AF37", letters: "#E8C66A" },
  { id: "brass", label: "Latón", swatch: "linear-gradient(135deg,#f6dfae,#b8893c 55%,#5b3d12)", hex: "#B8893C", letters: "#E2BD70" },
  { id: "silver", label: "Plata", swatch: "linear-gradient(135deg,#ffffff,#d9dee6 55%,#7d8796)", hex: "#D9DEE6", letters: "#E6EDF5" },
];

export const getTextureMaterial = (partValue) =>
  TEXTURE_MATERIALS.find((entry) => entry.id === partValue) || null;
export const getColorMaterial = (partValue) =>
  COLOR_MATERIALS.find((entry) => entry.id === partValue) || null;
export const getTrimOption = (trimId) =>
  TRIM_OPTIONS.find((entry) => entry.id === trimId) || TRIM_OPTIONS[0];

// ---------------------------------------------------------------------------
// Diseño del tablero (5 partes)
// ---------------------------------------------------------------------------

export const DEFAULT_BOARD_DESIGN = Object.freeze({
  preset: "clasico",
  light: "orig",
  dark: "orig",
  wall: "orig",
  trim: "orig",
});

const PART_PATTERN = /^(orig|tex:[a-z0-9-]+|col:[0-9a-f]{6}|flag:flag-[a-z-]+|wall:[a-z0-9-]+)$/;
const cleanPart = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return PART_PATTERN.test(normalized) ? normalized : "orig";
};
const cleanTrim = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return TRIM_OPTIONS.some((entry) => entry.id === normalized) ? normalized : "orig";
};

// Las casillas no llevan banderas (las de antes vuelven a lo clásico).
const cleanTilePart = (value) => {
  const part = cleanPart(value);
  return part.startsWith("flag:") ? "orig" : part;
};

export const normalizeBoardDesign = (design) => ({
  preset: String(design?.preset || "custom").trim().toLowerCase().replace(/[^a-z0-9-]/g, "") || "custom",
  light: cleanTilePart(design?.light),
  dark: cleanTilePart(design?.dark),
  wall: cleanPart(design?.wall),
  trim: cleanTrim(design?.trim),
});

export const serializeBoardDesign = (design) => {
  const value = normalizeBoardDesign(design);
  return ["v2", value.preset, value.light, value.dark, value.wall, value.trim].join("|");
};

export const parseBoardDesign = (raw) => {
  const parts = String(raw || "").trim().split("|");
  if (parts.length < 6 || parts[0] !== "v2") return null;
  return normalizeBoardDesign({
    preset: parts[1],
    light: parts[2],
    dark: parts[3],
    wall: parts[4],
    trim: parts[5],
  });
};

export const readStoredBoardDesign = () => {
  try {
    return parseBoardDesign(window.localStorage.getItem(BOARD_DESIGN_STORAGE_KEY));
  } catch (error) {
    return null;
  }
};

export const writeStoredBoardDesign = (design) => {
  try {
    window.localStorage.setItem(BOARD_DESIGN_STORAGE_KEY, serializeBoardDesign(design));
  } catch (error) {}
};

export const sameBoardParts = (a, b) =>
  ["light", "dark", "wall", "trim"].every((key) => (a?.[key] || "orig") === (b?.[key] || "orig"));

// ---------------------------------------------------------------------------
// Tableros completos
// ---------------------------------------------------------------------------

const boardThumb = (name) => studioAsset(`boards/${name}.jpg`);

// themeId: tema antiguo para las casillas oscuras cuando quedan en "orig".
export const BOARD_PRESETS = [
  {
    id: "clasico",
    label: "Clásico Xima",
    subtitle: "Original del juego",
    thumb: boardThumb("clasico"),
    tags: ["elegantes", "neon"],
    themeId: "classic",
    parts: { light: "orig", dark: "orig", wall: "orig", trim: "orig" },
    wallTextureId: "default",
  },
  {
    id: "nogal-arce",
    label: "Nogal y arce",
    subtitle: "Madera noble",
    thumb: boardThumb("madera"),
    tags: ["elegantes", "naturaleza"],
    themeId: "classic",
    parts: { light: "tex:arce", dark: "tex:nogal", wall: "tex:nogal-oscuro", trim: "brass" },
  },
  {
    id: "marmol-esmeralda",
    label: "Mármol esmeralda",
    subtitle: "Mármol y oro",
    thumb: boardThumb("marmol"),
    tags: ["elegantes"],
    themeId: "classic",
    parts: { light: "tex:marmol-blanco", dark: "tex:marmol-esmeralda", wall: "tex:marmol-verde-oscuro", trim: "gold" },
  },
  {
    id: "cesped",
    label: "Césped",
    subtitle: "Campo recién cortado",
    thumb: boardThumb("cesped"),
    tags: ["naturaleza"],
    themeId: "classic",
    parts: { light: "tex:cesped", dark: "tex:musgo", wall: "tex:nogal-oscuro", trim: "brass" },
  },
  {
    id: "agua",
    label: "Agua cristalina",
    subtitle: "Piscina tropical",
    thumb: boardThumb("agua"),
    tags: ["naturaleza"],
    themeId: "classic",
    parts: { light: "tex:agua", dark: "tex:oceano", wall: "tex:azul-noche", trim: "silver" },
  },
  // Los temas de antes siguen disponibles: solo cambian las casillas oscuras.
  ...[
    { id: "cosmic", label: "Azul cósmico", filter: "hue-rotate(55deg) saturate(1.2)" },
    { id: "magma", label: "Magma", filter: "hue-rotate(185deg) saturate(1.4)" },
    { id: "emerald", label: "Madera clásica", filter: "sepia(0.85) saturate(1.6) hue-rotate(-12deg)" },
    { id: "royal", label: "Royal", filter: "hue-rotate(95deg) saturate(1.3)" },
    { id: "mono", label: "Noche", filter: "grayscale(0.9) brightness(0.85)" },
  ].map((theme) => ({
    id: `tema-${theme.id}`,
    label: theme.label,
    subtitle: "Tema clásico",
    thumb: boardThumb("clasico"),
    thumbFilter: theme.filter,
    tags: ["neon"],
    themeId: theme.id,
    parts: { light: "orig", dark: "orig", wall: "orig", trim: "orig" },
    wallTextureId: "default",
  })),
];

export const BOARD_FILTERS = [
  { id: "all", label: "Todos" },
  { id: "mine", label: "Mis tableros" },
  { id: "elegantes", label: "Elegantes" },
  { id: "naturaleza", label: "Naturaleza" },
  { id: "neon", label: "Neón" },
];

export const readMyBoards = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(MY_BOARDS_STORAGE_KEY) || "[]");
    return Array.isArray(parsed)
      ? parsed
          .filter((entry) => entry && entry.id && entry.parts)
          .map((entry) => ({ ...entry, parts: normalizeBoardDesign(entry.parts) }))
      : [];
  } catch (error) {
    return [];
  }
};

export const writeMyBoards = (boards) => {
  try {
    window.localStorage.setItem(MY_BOARDS_STORAGE_KEY, JSON.stringify(boards.slice(0, 24)));
  } catch (error) {}
};

// ---------------------------------------------------------------------------
// Bloques nuevos (sirven para bloque común y especial)
// ---------------------------------------------------------------------------

const blockThumb = (name) => studioAsset(`blocks/${name}.png`);

export const BLOCK_STYLES = [
  { id: "xs-star", label: "Estrella clásica", category: "clasicos", rarity: "COMÚN", thumb: blockThumb("star-red"), thumbByColor: true, description: "Lacado brillante del color de tu equipo con estrellas doradas en relieve." },
  { id: "xs-plain", label: "Liso", category: "clasicos", rarity: "COMÚN", thumb: blockThumb("plain-red"), description: "Lacado brillante del color de tu equipo, sin dibujos." },
  { id: "xs-marble", label: "Mármol blanco", category: "materiales", rarity: "RARO", thumb: blockThumb("marble"), description: "Mármol blanco pulido con vetas grises." },
  { id: "xs-emerald", label: "Mármol esmeralda", category: "materiales", rarity: "RARO", thumb: blockThumb("emerald"), description: "Mármol verde con vetas claras, pulido como espejo." },
  { id: "xs-wood", label: "Madera", category: "materiales", rarity: "RARO", thumb: blockThumb("wood"), description: "Nogal barnizado." },
  { id: "xs-gold", label: "Oro", category: "materiales", rarity: "LEGENDARIO", thumb: blockThumb("gold"), description: "Oro macizo y brillante." },
  { id: "xs-chrome", label: "Cromo", category: "materiales", rarity: "LEGENDARIO", thumb: blockThumb("chrome"), description: "Metal cromado que refleja todo el tablero." },
  { id: "xs-lava", label: "Lava", category: "naturaleza", rarity: "ÉPICO", thumb: blockThumb("lava"), description: "Roca volcánica con grietas de lava que brillan." },
  { id: "xs-water", label: "Agua", category: "naturaleza", rarity: "ÉPICO", thumb: blockThumb("water"), description: "Agua cristalina de piscina." },
  { id: "xs-grass", label: "Césped", category: "naturaleza", rarity: "RARO", thumb: blockThumb("grass"), description: "Césped recién cortado, mate." },
  { id: "xs-neon", label: "Neón cian", category: "neon", rarity: "ÉPICO", thumb: blockThumb("neon"), description: "Bordes de luz cian que brillan en la oscuridad." },
  { id: "xs-neon-pink", label: "Neón rosa", category: "neon", rarity: "ÉPICO", thumb: blockThumb("neon-pink"), description: "Bordes de luz rosa que brillan en la oscuridad." },
  { id: "xs-galaxy", label: "Galaxia", category: "galaxia", rarity: "ÉPICO", thumb: blockThumb("galaxy"), description: "Un trozo del universo con nebulosas moradas." },
];

export const BLOCK_STYLE_STAR_THUMBS = {
  blue: blockThumb("star-blue"),
  red: blockThumb("star-red"),
  green: blockThumb("star-green"),
  yellow: blockThumb("star-yellow"),
};

export const getBlockStyle = (id) => BLOCK_STYLES.find((entry) => entry.id === id) || null;

export const BLOCK_FILTERS = [
  { id: "all", label: "Todos" },
  { id: "clasicos", label: "Clásicos" },
  { id: "materiales", label: "Materiales" },
  { id: "naturaleza", label: "Naturaleza" },
  { id: "banderas", label: "Banderas" },
  { id: "neon", label: "Neón" },
  { id: "galaxia", label: "Galaxia" },
];

export const STATUS_FILTERS = [
  { id: "all", label: "Todos" },
  { id: "free", label: "Gratis" },
  { id: "won", label: "Ganados" },
  { id: "shop", label: "Tienda" },
];

// Colores de equipo de la vista previa (Así se ve en tu tablero).
export const TEAM_COLORS = [
  { id: "red", hex: "#e11d48", game: "#ff003d" },
  { id: "blue", hex: "#1d4ed8", game: "#005bff" },
  { id: "green", hex: "#16a34a", game: "#04ff17" },
  { id: "yellow", hex: "#eab308", game: "#ffea00" },
  { id: "white", hex: "#f4f4f5", game: "#f0f0f0" },
  { id: "gray", hex: "#71717a", game: "#80858c" },
];
